import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, OnDestroy } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class RightFlyoutOverlayService implements OnDestroy {
    private observer: MutationObserver | null = null;
    private isStarted = false;
    private refreshTimeout: number | null = null;

    constructor(@Inject(DOCUMENT) private document: Document) {}

    start(): void {
        if (this.isStarted || !this.document?.body) {
            return;
        }

        this.isStarted = true;

        this.observer = new MutationObserver(() => this.scheduleRefresh());
        this.observer.observe(this.document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ['class', 'style']
        });

        this.refreshBlurClass();
    }

    stop(): void {
        if (!this.isStarted) {
            return;
        }

        this.isStarted = false;

        if (this.refreshTimeout !== null) {
            window.clearTimeout(this.refreshTimeout);
            this.refreshTimeout = null;
        }

        this.observer?.disconnect();
        this.observer = null;

        this.document.body?.classList.remove('sidebar-open');
    }

    ngOnDestroy(): void {
        this.stop();
    }

    private scheduleRefresh(): void {
        if (this.refreshTimeout !== null) {
            window.clearTimeout(this.refreshTimeout);
        }

        this.refreshTimeout = window.setTimeout(() => {
            this.refreshTimeout = null;
            this.refreshBlurClass();
        }, 0);
    }

    private refreshBlurClass(): void {
        if (!this.document?.body) {
            return;
        }

        if (this.hasActiveRightFlyout()) {
            this.document.body.classList.add('sidebar-open');
            return;
        }

        this.document.body.classList.remove('sidebar-open');
    }

    private hasActiveRightFlyout(): boolean {
        const overlayMasks = Array.from(
            this.document.querySelectorAll('.p-drawer-mask.p-overlay-mask, .p-sidebar-mask.p-component-overlay')
        ) as HTMLElement[];

        for (const mask of overlayMasks) {
            if (!this.isElementVisible(mask)) {
                continue;
            }

            const rightFlyout = mask.querySelector('.p-drawer.p-drawer-right, .p-sidebar.p-sidebar-right') as HTMLElement | null;
            if (!rightFlyout) {
                continue;
            }

            if (this.hasOpenState(rightFlyout) && this.isElementVisible(rightFlyout)) {
                return true;
            }
        }

        const rightFlyouts = Array.from(
            this.document.querySelectorAll('.p-drawer.p-drawer-right, .p-sidebar.p-sidebar-right')
        ) as HTMLElement[];

        for (const flyout of rightFlyouts) {
            if (this.hasOpenState(flyout) && this.isElementVisible(flyout)) {
                return true;
            }
        }

        return false;
    }

    private hasOpenState(element: Element): boolean {
        // PrimeNG 21 keeps .p-drawer-open on the leaving panel until the animation
        // ends. data-p-open flips off as soon as the panel is closed, so use that
        // or the page stays overflow-locked after Save.
        if (element.classList.contains('p-drawer') || element.hasAttribute('data-p-open')) {
            return element.getAttribute('data-p-open') === 'true';
        }

        return (
            element.classList.contains('p-sidebar-open') ||
            element.classList.contains('p-sidebar-active') ||
            element.classList.contains('p-sidebar-visible')
        );
    }

    private isElementVisible(element: HTMLElement): boolean {
        const style = window.getComputedStyle(element);
        if (style.display === 'none' || style.visibility === 'hidden') {
            return false;
        }

        const rect = element.getBoundingClientRect();
        if (rect.width <= 0 || rect.height <= 0) {
            return false;
        }

        const viewportWidth = window.innerWidth || this.document.documentElement.clientWidth;
        const viewportHeight = window.innerHeight || this.document.documentElement.clientHeight;

        return rect.right > 0 && rect.bottom > 0 && rect.left < viewportWidth && rect.top < viewportHeight;
    }
}
